// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2023-03-03 13:43:32 +0100 (Fri, 03 Mar 2023) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 132664 $
// Purpose     : debug sagas
// =============================================================================
// ---Third-party imports
import {select, delay} from 'uix/sagas'
import {put, takeEvery, call, throttle, all, putResolve, fork, race, take} from 'redux-saga/effects'
//~ import localForage from 'localforage'
import {replace} from 'libjs/redux-little-router'
import axios from 'axios'

// ---Framework imports
import {has, get, mapItems, dict, flatten, rstrip, range, size, zip, keys} from 'ldash'

import Api from 'uix/api'
import {Actions, Sel} from 'uix/registries'
import {isDebugOn, getSessionId, getRunId} from 'uix/selectors'
import log from 'uix/logging'
import {hashObj} from 'uix/utils'
import {apiurlToPrefix} from 'uix/deploy'
import {getLocalStorageInfo} from 'uix/persist/sagas'

// ---Local imports
import {_triggerSagaError} from './errors'
import {
  goRandomId,
  interpreterCmd,
  setInterpreterOutput,
  setDebugData,
  getApiAvailability,
  switchApiUrl,
  triggerSagaError,
  hotUpdate,
} from './actions'

import {pprint, PPRINT_BUFFER} from './pprint'

// ---Globals

function* _goRandomId({payload}) {
  let {data, error} = yield call(Api.get, '/api/getRandomMetadataId', {
    minPages: payload.minPages || 0,
  })
  if (!error) {
    if (!data.id) {
      log.error('got no id')
    } else {
      yield put(Actions.go({...payload, id: data.id}))
    }
  }
}

function* watchPPrintBuffer() {
  let INTERVAL = 50
  let lastObj = null
  let lastObjHash = null
  while (true) {
    if (lastObj !== PPRINT_BUFFER.data) {
      //~ log('new pprint data %O', PPRINT_BUFFER.data)
      lastObj = PPRINT_BUFFER.data
      let h = hashObj(PPRINT_BUFFER.data)
      if (h != lastObjHash) {
        lastObjHash = h
        yield put(pprint(lastObj, PPRINT_BUFFER.expanded))
      }
    }
    yield delay(INTERVAL)
  }
}

function* _interpreterCmd({payload}) {
  let {cmd, multiline} = payload
  let {data, error} = yield Api.post('/api/interpreter', {cmd, multiline}, {timeout: Infinity})
  if (error) {
    log.error('error during interpreter request: %o', error)
  } else {
    yield put(setInterpreterOutput(data.result))
  }
}

function* _isAvailable(url) {
  let {error} = yield Api.post(
    `${url}/api`,
    {},
    {timeout: 1000},
    {silent: true, silentErrors: true}
  )
  let apiAvailability = yield select(root => root.debug.apiAvailability || {})
  yield put(setDebugData({apiAvailability: {...apiAvailability, [url]: !error}}))
}

function* _getApiAvailability({payload}) {
  let {urls} = payload
  yield all(urls.map(url => call(_isAvailable, url)))
}

function* _switchApiUrl({payload}) {
  let {apiurl} = payload
  let pathPrefix = apiurlToPrefix(apiurl)
  let prevRequest = yield select(root => root.meta.prev_request)
  let prevClassname = get(prevRequest, 'classname')
  let prevTabname = get(prevRequest, 'tabname')

  let path = prevClassname ? `/${prevClassname}` : ''
  path += prevTabname ? `/${prevTabname}` : ''

  //~ let fullpath = `${pathPrefix}`
  let runId = yield select(getRunId)
  yield putResolve(replace({pathname: `/${pathPrefix}${path}`, query: {}}, {runId}))
  yield put(Actions.setAppState({state: 'INIT_APP', deploy: {apiurl, pathPrefix}}))
}

function* watchHotUpdate() {
  /* in webpack dev-server mode, local domain css files will be written to window.VLS_DOMAIN_CSS
  and integrated here
  */
  while (true) {
    // checking periodically handles initialization
    yield race([delay(2000), take(hotUpdate)])

    let local_domaincss = get(window, 'VLS_DOMAIN_CSS', {})

    let dcss = yield select(root => root.config.dcss)
    let dnames = keys(local_domaincss) //yield select(root => get(root.config, 'domains.names', []))

    let data = dnames
      .filter(dn => local_domaincss[dn])
      .map(dn => {
        let files = local_domaincss[dn]
        let key = `${dn}.hotfs`
        if (dcss[key] !== files) {
          return [key, files]
        }
      })
      .filter(e => e)

    if (size(data) > 0) {
      log.info('new domain css files from fs: %o', data.map(([dn]) => dn))
      yield put(Actions.setDomainCSS(dict(data)))
    }
  }
}

let loopers = {}

// perma loopers
if (process.env.UIX_ENV.includes('browser')) {
  if (process.env.NODE_ENV !== 'production') {
    loopers.watchHotUpdate = watchHotUpdate()
  }
  // also allow in prod
  loopers.watchPPrintBuffer = watchPPrintBuffer()

  // I: fix ACTIVATE_VIEW!
  // II: throttle(10000) blocks ssr saga END resolving
  loopers.getLocalStorageInfo = throttle(10000, 'ACTIVATE_VIEW', getLocalStorageInfo)
}

function* _serverMessage({type}) {
  let isDebugOn = yield select(Sel.isDebugOn)
  let isDevServer = yield select(root => root.config.instance.info.mode == 'dev')
  if (isDebugOn || isDevServer) {
    let [_, msgtype] = type.split('/')
    yield put(Actions.showNotification({header: `Server ${msgtype}`}))
  }
}

export const sagas = {
  goRandomId: takeEvery(goRandomId, _goRandomId),
  interpreterCmd: takeEvery(interpreterCmd, _interpreterCmd),
  getApiAvailability: takeEvery(getApiAvailability, _getApiAvailability),
  switchApiUrl: takeEvery(switchApiUrl, _switchApiUrl),
  triggerSagaError: takeEvery(triggerSagaError, _triggerSagaError),
  serverDown: takeEvery('SERVER/SHUTDOWN', _serverMessage),
  serverDead: takeEvery('SERVER/DEAD', _serverMessage),
  serverUp: takeEvery('SERVER/STARTUP', _serverMessage),

  //~ hotUpdate: takeEvery(hotUpdate, _hotUpdate),

  ...loopers,
}
